import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import type { VirtualTourNode } from '@indutrust/factory-model';
import { resolveHttpError, withRequestStatus } from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore, withState } from '@ngrx/signals';
import {
  Events,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { map, switchMap } from 'rxjs';
import { VrLinkApiClient } from '../vr-link-api-client.service';
import { VrMarkerApiClient } from '../vr-marker-api-client.service';
import { VrNodeActionsApiClient } from '../vr-node-actions-api-client.service';
import { VrNodeApiClient } from '../vr-node-api-client.service';
import { loadVirtualTourGraph } from './virtual-tour-load.util';
import { virtualTourEvents } from './virtual-tour.events';

type VirtualTourState = {
  factoryId: number | null;
  nodes: VirtualTourNode[];
  saving: boolean;
};

const initialState: VirtualTourState = {
  factoryId: null,
  nodes: [],
  saving: false,
};

/**
 * Component-scoped store owning a single factory's virtual-tour graph.
 *
 * Provided by {@link import('@indutrust/factory-feature').FactoryView | FactoryView}
 * (read-only) and {@link import('@indutrust/factory-feature').FactoryEdit | FactoryEdit}
 * (authoring) so each surface gets a fresh graph.
 *
 * `loadRequested` fetches the node collection and fans out one
 * links + markers request per node, composing the result into
 * {@link VirtualTourNode}s. Authoring mutations (node / link / marker CRUD
 * and panorama upload) re-emit `mutationSucceeded`, which re-triggers a
 * reload — the graph is small enough that a refetch is simpler and safer
 * than optimistic patching.
 *
 * @example
 * const tourStore = inject(VirtualTourStore);
 * const dispatch = injectDispatch(virtualTourEvents);
 * dispatch.loadRequested({ factoryId: 7 });
 */
export const VirtualTourStore = signalStore(
  withState(initialState),
  withRequestStatus(),
  withReducer(
    on(virtualTourEvents.loadRequested, ({ payload }) => ({
      factoryId: payload.factoryId,
      requestStatus: 'loading',
    })),
    on(virtualTourEvents.loadSucceeded, ({ payload }) => ({
      nodes: payload.nodes,
      requestStatus: 'success',
    })),
    on(virtualTourEvents.loadFailed, ({ payload }) => ({
      requestStatus: { error: payload.error },
    })),
    on(virtualTourEvents.nodeCreateRequested, () => ({ saving: true })),
    on(virtualTourEvents.nodeUpdateRequested, () => ({ saving: true })),
    on(virtualTourEvents.nodeDeleteRequested, () => ({ saving: true })),
    on(virtualTourEvents.panoramaUploadRequested, () => ({ saving: true })),
    on(virtualTourEvents.linkCreateRequested, () => ({ saving: true })),
    on(virtualTourEvents.linkDeleteRequested, () => ({ saving: true })),
    on(virtualTourEvents.linkUpdateRequested, () => ({ saving: true })),
    on(virtualTourEvents.markerCreateRequested, () => ({ saving: true })),
    on(virtualTourEvents.markerDeleteRequested, () => ({ saving: true })),
    on(virtualTourEvents.markerUpdateRequested, () => ({ saving: true })),
    on(virtualTourEvents.mutationSucceeded, () => ({ saving: false })),
    on(virtualTourEvents.mutationFailed, ({ payload }) => ({
      saving: false,
      requestStatus: { error: payload.error },
    }))
  ),
  withEventHandlers(
    (
      _,
      events = inject(Events),
      nodes = inject(VrNodeApiClient),
      links = inject(VrLinkApiClient),
      markers = inject(VrMarkerApiClient),
      nodeActions = inject(VrNodeActionsApiClient)
    ) => {
      const toMutationFailed = (error: HttpErrorResponse) =>
        virtualTourEvents.mutationFailed({
          error: resolveHttpError(error).code,
        });

      return {
        load$: events.on(virtualTourEvents.loadRequested).pipe(
          switchMap(({ payload }) =>
            nodes.getAll({ factoryId: payload.factoryId }).pipe(
              switchMap((nodeList) =>
                loadVirtualTourGraph(nodeList, links, markers)
              ),
              mapResponse({
                next: (graph) =>
                  virtualTourEvents.loadSucceeded({
                    factoryId: payload.factoryId,
                    nodes: graph,
                  }),
                error: (error: HttpErrorResponse) =>
                  virtualTourEvents.loadFailed({
                    error: resolveHttpError(error).code,
                  }),
              })
            )
          )
        ),
        nodeCreate$: events.on(virtualTourEvents.nodeCreateRequested).pipe(
          switchMap(({ payload }) =>
            nodes
              .add(
                { factory_id: payload.factoryId, name: payload.name },
                { factoryId: payload.factoryId }
              )
              .pipe(
                mapResponse({
                  next: () =>
                    virtualTourEvents.mutationSucceeded({
                      factoryId: payload.factoryId,
                    }),
                  error: toMutationFailed,
                })
              )
          )
        ),
        nodeUpdate$: events.on(virtualTourEvents.nodeUpdateRequested).pipe(
          switchMap(({ payload }) =>
            nodes
              .patch(
                payload.nodeId,
                { name: payload.name },
                { factoryId: payload.factoryId }
              )
              .pipe(
                mapResponse({
                  next: () =>
                    virtualTourEvents.mutationSucceeded({
                      factoryId: payload.factoryId,
                    }),
                  error: toMutationFailed,
                })
              )
          )
        ),
        nodeDelete$: events.on(virtualTourEvents.nodeDeleteRequested).pipe(
          switchMap(({ payload }) =>
            nodes.delete(payload.nodeId, { factoryId: payload.factoryId }).pipe(
              mapResponse({
                next: () =>
                  virtualTourEvents.mutationSucceeded({
                    factoryId: payload.factoryId,
                  }),
                error: toMutationFailed,
              })
            )
          )
        ),
        panoramaUpload$: events
          .on(virtualTourEvents.panoramaUploadRequested)
          .pipe(
            switchMap(({ payload }) =>
              nodeActions.updatePanorama(payload.nodeId, payload.file).pipe(
                mapResponse({
                  next: () =>
                    virtualTourEvents.mutationSucceeded({
                      factoryId: payload.factoryId,
                    }),
                  error: toMutationFailed,
                })
              )
            )
          ),
        linkCreate$: events.on(virtualTourEvents.linkCreateRequested).pipe(
          switchMap(({ payload }) =>
            links.add(payload.link, { nodeId: payload.nodeId }).pipe(
              mapResponse({
                next: () =>
                  virtualTourEvents.mutationSucceeded({
                    factoryId: payload.factoryId,
                  }),
                error: toMutationFailed,
              })
            )
          )
        ),
        linkDelete$: events.on(virtualTourEvents.linkDeleteRequested).pipe(
          switchMap(({ payload }) =>
            links.delete(payload.linkId, { nodeId: payload.nodeId }).pipe(
              mapResponse({
                next: () =>
                  virtualTourEvents.mutationSucceeded({
                    factoryId: payload.factoryId,
                  }),
                error: toMutationFailed,
              })
            )
          )
        ),
        linkUpdate$: events.on(virtualTourEvents.linkUpdateRequested).pipe(
          switchMap(({ payload }) =>
            links
              .patch(payload.linkId, payload.link, { nodeId: payload.nodeId })
              .pipe(
                mapResponse({
                  next: () =>
                    virtualTourEvents.mutationSucceeded({
                      factoryId: payload.factoryId,
                    }),
                  error: toMutationFailed,
                })
              )
          )
        ),
        markerCreate$: events.on(virtualTourEvents.markerCreateRequested).pipe(
          switchMap(({ payload }) =>
            markers.add(payload.marker, { nodeId: payload.nodeId }).pipe(
              mapResponse({
                next: () =>
                  virtualTourEvents.mutationSucceeded({
                    factoryId: payload.factoryId,
                  }),
                error: toMutationFailed,
              })
            )
          )
        ),
        markerDelete$: events.on(virtualTourEvents.markerDeleteRequested).pipe(
          switchMap(({ payload }) =>
            markers.delete(payload.markerId, { nodeId: payload.nodeId }).pipe(
              mapResponse({
                next: () =>
                  virtualTourEvents.mutationSucceeded({
                    factoryId: payload.factoryId,
                  }),
                error: toMutationFailed,
              })
            )
          )
        ),
        markerUpdate$: events.on(virtualTourEvents.markerUpdateRequested).pipe(
          switchMap(({ payload }) =>
            markers
              .patch(payload.markerId, payload.marker, {
                nodeId: payload.nodeId,
              })
              .pipe(
                mapResponse({
                  next: () =>
                    virtualTourEvents.mutationSucceeded({
                      factoryId: payload.factoryId,
                    }),
                  error: toMutationFailed,
                })
              )
          )
        ),
        refreshOnMutation$: events
          .on(virtualTourEvents.mutationSucceeded)
          .pipe(
            map(({ payload }) =>
              virtualTourEvents.loadRequested({ factoryId: payload.factoryId })
            )
          ),
      };
    }
  )
);
